import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Checkbox,
  FormControlLabel,
  Divider,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import ToothSelectionGrid from "./ToothSelectionGrid";
import styles from "./FP17DCModal.module.scss";
import axios from "axios";
import { toast } from "react-toastify";

type FP17DCModalProps = {
  open: boolean;
  onClose: () => void;
  consentBundleId?: string | number;
  treatmentData?: any;
  setTreatmentData?: React.Dispatch<React.SetStateAction<any>>;
};

type FormDataType = {
  provider_details: string;
  performer_number: string;
  patient_surname: string;
  patient_forename: string;
  patient_dob: string;
  patient_date_of_acceptance: string;

  patient_date_of_examination: string;
  treatment_on_referral_only: string;
  care_and_treatment_required: string;

  no_treatment_required: string;
  checkup_in_months: string;

  nhs_treatment_examination_advice: string;
  nhs_treatment_radiographs: string;
  nhs_treatment_prevention: string;
  nhs_treatment_scaling_polishing: string;
  nhs_treatment_adjustment_easing_denture: string;
  nhs_treatment_diagnosis_other: string;

  nhs_treatment_non_surgical_periodontal: string;
  nhs_treatment_surgical_periodontal: string;
  nhs_treatment_permenant_filling: string;
  nhs_treatment_endodontics: string;
  nhs_treatment_extraction_other_oral: string;
  nhs_treatment_occlusal_splints: string;
  nhs_treatment_relining_rebasing: string;
  nhs_treatment_other: string;

  nhs_treatment_veneers_pinlays: string;
  nhs_treatment_bridges: string;
  nhs_treatment_dentures: string;
  nhs_treatment_appliances_other: string;
  nhs_treatment_other_custom: string;

  prv_treatment_radiographs: string;
  prv_treatment_prevention: string;
  prv_treatment_scaling: string;
  diagnosis4: string;

  prv_treatment_non_surgical_periodontal: string;
  prv_treatment_surgical_periodontal: string;
  prv_treatment_permenant_filling: string;
  prv_treatment_endodontics: string;
  prv_treatment_extractions: string;
  prv_treatment_other: string;

  prv_treatment_veneers_pinlays: string;
  prv_treatment_bridges: string;
  prv_treatment_dentures: string;
  prv_treatment_appliances_others: string;
  prv_treatment_other_custom: string;

  additional_observations: string;

  charges_band_1_nhs: string;
  charges_band_2_nhs: string;
  charges_band_3_nhs: string;

  charge_for_nhs_treatment: string;
  charge_for_private_treatment: string;
};

const yesNo = (value: any) => (value === "Yes" ? "Yes" : "No");

const stringValue = (value: any) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const FP17DCModal = ({
  open,
  onClose,
  consentBundleId,
  treatmentData,
  setTreatmentData,
}: FP17DCModalProps) => {
  const fp17dcData = treatmentData?.fp17dc_data || {};

  const initialFormData = useMemo<FormDataType>(
    () => ({
      provider_details: stringValue(fp17dcData?.provider_details),
      performer_number: stringValue(fp17dcData?.performer_number),
      patient_surname: stringValue(fp17dcData?.patient_surname),
      patient_forename: stringValue(fp17dcData?.patient_forename),
      patient_dob: stringValue(fp17dcData?.patient_dob),
      patient_date_of_acceptance: stringValue(fp17dcData?.patient_date_of_acceptance),

      patient_date_of_examination: stringValue(fp17dcData?.patient_date_of_examination),
      treatment_on_referral_only: yesNo(fp17dcData?.treatment_on_referral_only),
      care_and_treatment_required: stringValue(fp17dcData?.care_and_treatment_required),

      no_treatment_required: stringValue(fp17dcData?.no_treatment_required),
      checkup_in_months: stringValue(fp17dcData?.checkup_in_months),

      nhs_treatment_examination_advice: yesNo(fp17dcData?.nhs_treatment_examination_advice),
      nhs_treatment_radiographs: yesNo(fp17dcData?.nhs_treatment_radiographs),
      nhs_treatment_prevention: yesNo(fp17dcData?.nhs_treatment_prevention),
      nhs_treatment_scaling_polishing: yesNo(fp17dcData?.nhs_treatment_scaling_polishing),
      nhs_treatment_adjustment_easing_denture: yesNo(fp17dcData?.nhs_treatment_adjustment_easing_denture),
      nhs_treatment_diagnosis_other: yesNo(fp17dcData?.nhs_treatment_diagnosis_other),

      nhs_treatment_non_surgical_periodontal: yesNo(fp17dcData?.nhs_treatment_non_surgical_periodontal),
      nhs_treatment_surgical_periodontal: yesNo(fp17dcData?.nhs_treatment_surgical_periodontal),
      nhs_treatment_permenant_filling: yesNo(fp17dcData?.nhs_treatment_permenant_filling),
      nhs_treatment_endodontics: yesNo(fp17dcData?.nhs_treatment_endodontics),
      nhs_treatment_extraction_other_oral: yesNo(fp17dcData?.nhs_treatment_extraction_other_oral),
      nhs_treatment_occlusal_splints: yesNo(fp17dcData?.nhs_treatment_occlusal_splints),
      nhs_treatment_relining_rebasing: yesNo(fp17dcData?.nhs_treatment_relining_rebasing),
      nhs_treatment_other: yesNo(fp17dcData?.nhs_treatment_other),

      nhs_treatment_veneers_pinlays: yesNo(fp17dcData?.nhs_treatment_veneers_pinlays),
      nhs_treatment_bridges: yesNo(fp17dcData?.nhs_treatment_bridges),
      nhs_treatment_dentures: yesNo(fp17dcData?.nhs_treatment_dentures),
      nhs_treatment_appliances_other: yesNo(fp17dcData?.nhs_treatment_appliances_other),
      nhs_treatment_other_custom: stringValue(fp17dcData?.nhs_treatment_other_custom),

      prv_treatment_radiographs: stringValue(fp17dcData?.prv_treatment_radiographs),
      prv_treatment_prevention: stringValue(fp17dcData?.prv_treatment_prevention),
      prv_treatment_scaling: stringValue(fp17dcData?.prv_treatment_scaling),
      diagnosis4: stringValue(fp17dcData?.prv_treatment_diagnosis_other),

      prv_treatment_non_surgical_periodontal: stringValue(fp17dcData?.prv_treatment_non_surgical_periodontal),
      prv_treatment_surgical_periodontal: stringValue(fp17dcData?.prv_treatment_surgical_periodontal),
      prv_treatment_permenant_filling: stringValue(fp17dcData?.prv_treatment_permenant_filling),
      prv_treatment_endodontics: stringValue(fp17dcData?.prv_treatment_endodontics),
      prv_treatment_extractions: stringValue(fp17dcData?.prv_treatment_extractions),
      prv_treatment_other: stringValue(fp17dcData?.prv_treatment_other),

      prv_treatment_veneers_pinlays: stringValue(fp17dcData?.prv_treatment_veneers_pinlays),
      prv_treatment_bridges: stringValue(fp17dcData?.prv_treatment_bridges),
      prv_treatment_dentures: stringValue(fp17dcData?.prv_treatment_dentures),
      prv_treatment_appliances_others: stringValue(fp17dcData?.prv_treatment_appliances_others),
      prv_treatment_other_custom: stringValue(fp17dcData?.prv_treatment_other_custom),

      additional_observations: stringValue(fp17dcData?.additional_observations),

      charges_band_1_nhs: yesNo(fp17dcData?.charges_band_1_nhs),
      charges_band_2_nhs: yesNo(fp17dcData?.charges_band_2_nhs),
      charges_band_3_nhs: yesNo(fp17dcData?.charges_band_3_nhs),

      charge_for_nhs_treatment: stringValue(fp17dcData?.charge_for_nhs_treatment),
      charge_for_private_treatment: stringValue(fp17dcData?.charge_for_private_treatment),
    }),
    [fp17dcData]
  );

  const getInitialSelectedTeeth = (): Record<string, string> => {
    const toothKeys = [
      "tooth_ur8", "tooth_ur7", "tooth_ur6", "tooth_ur5", "tooth_ur4", "tooth_ur3", "tooth_ur2", "tooth_ur1",
      "tooth_ul1", "tooth_ul2", "tooth_ul3", "tooth_ul4", "tooth_ul5", "tooth_ul6", "tooth_ul7", "tooth_ul8",
      "tooth_lr8", "tooth_lr7", "tooth_lr6", "tooth_lr5", "tooth_lr4", "tooth_lr3", "tooth_lr2", "tooth_lr1",
      "tooth_ll1", "tooth_ll2", "tooth_ll3", "tooth_ll4", "tooth_ll5", "tooth_ll6", "tooth_ll7", "tooth_ll8",
    ];

    const mapped: Record<string, string> = {};
    toothKeys.forEach((key) => {
      mapped[key] = fp17dcData?.[key] === "Yes" ? "Yes" : "No";
    });
    return mapped;
  };

  const [formData, setFormData] = useState<FormDataType>(initialFormData);
  const [selectedTeeth, setSelectedTeeth] = useState<Record<string, string>>(getInitialSelectedTeeth());

  useEffect(() => {
    console.log("FP17DC treatmentData:", treatmentData);
    console.log("FP17DC fp17dc_data:", fp17dcData);
  }, [treatmentData, fp17dcData]);

  useEffect(() => {
  if (open) {
    setFormData(initialFormData);
    setSelectedTeeth(getInitialSelectedTeeth());
  }
}, [open]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type, checked } = target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? "Yes" : "No") : value,
    }));
  };

  const handleToothSelect = (toothKey: string) => {
    setSelectedTeeth((prev) => ({
      ...prev,
      [toothKey]: prev?.[toothKey] === "Yes" ? "No" : "Yes",
    }));
  };

  const handleSave = async () => {
    try {
      const toothPayload = { ...selectedTeeth };

      const updatedFormData = {
        ...formData,
        ...toothPayload,
      };

      const response = await axios.post(
        `/consent_bundle/${consentBundleId}/save_fp17dc_form_data`,
        updatedFormData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const result = response.data;

      console.log("FP17DC save result:", result);
      toast.success("FP17DC data saved successfully");

      if (setTreatmentData) {
        setTreatmentData((prev: any) => ({
          ...prev,
          fp17dc_data: {
            ...(prev?.fp17dc_data || {}),
            ...updatedFormData,
          },
        }));
      }

      onClose();
    } catch (error: any) {
      console.error("Error saving form data:", error);
      toast.error(`Failed to save form data: ${error?.message || "Unknown error"}`);
    }
  };

  const checkboxField = (name: keyof FormDataType, label: string) => (
    <FormControlLabel
      className={styles.checkboxLabel}
      control={
        <Checkbox
          checked={formData[name] === "Yes"}
          onChange={handleInputChange}
          name={name}
        />
      }
      label={label}
    />
  );

  const dateField = (
    name: keyof FormDataType,
    label: string,
    value: string
  ) => (
    <TextField
      fullWidth
      margin="normal"
      label={label}
      name={name}
      value={value}
      onChange={handleInputChange}
      type="date"
      InputLabelProps={{ shrink: true }}
    />
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modalRoot}>
        <Box className={styles.header}>
          <Box>
            <Typography className={styles.title}>
              Personal Dental Treatment Plan
            </Typography>
            <Typography className={styles.subtitle}>
              FP17DC form details
            </Typography>
          </Box>

          <Box className={styles.headerActions}>
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
          </Box>
        </Box>

        <Box
          component="form"
          className={styles.form}
          noValidate
          autoComplete="off"
          onSubmit={(e) => e.preventDefault()}
        >
          <Box className={styles.section}>
            <Typography className={styles.sectionTitle}>
              Patient and Provider Details
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Provider’s details"
                  name="provider_details"
                  value={formData.provider_details}
                  onChange={handleInputChange}
                  multiline
                  minRows={8}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Performer number"
                  name="performer_number"
                  value={formData.performer_number}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Surname"
                  name="patient_surname"
                  value={formData.patient_surname}
                  onChange={handleInputChange}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Forename"
                  name="patient_forename"
                  value={formData.patient_forename}
                  onChange={handleInputChange}
                />

                {dateField("patient_dob", "Date of Birth", formData.patient_dob)}

                {dateField(
                  "patient_date_of_acceptance",
                  "Date of Acceptance",
                  formData.patient_date_of_acceptance
                )}

                <Typography className={styles.noteText}>
                  The dentist named on this form is providing you with a course
                  of treatment. Information regarding your NHS dental treatment
                  is detailed overleaf.
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Box className={styles.section}>
            <Typography className={styles.sectionTitle}>
              Oral Health Assessment
            </Typography>

            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={4}>
                {dateField(
                  "patient_date_of_examination",
                  "Date of examination",
                  formData.patient_date_of_examination
                )}
              </Grid>

              <Grid item xs={12} md={4}>
                <Box className={styles.checkboxInline}>
                  {checkboxField(
                    "treatment_on_referral_only",
                    "Treatment on referral only"
                  )}
                </Box>
              </Grid>
            </Grid>

            <TextField
              fullWidth
              margin="normal"
              label="Care and Treatment required"
              name="care_and_treatment_required"
              value={formData.care_and_treatment_required}
              onChange={handleInputChange}
              multiline
              minRows={8}
            />
          </Box>

          <Box className={styles.section}>
            <Typography className={styles.sectionTitle}>
              Follow Up Recommendation
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="No Treatment required at this time"
                  name="no_treatment_required"
                  value={formData.no_treatment_required}
                  onChange={handleInputChange}
                  type="number"
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="I recommend a checkup in about months"
                  name="checkup_in_months"
                  value={formData.checkup_in_months}
                  onChange={handleInputChange}
                  type="number"
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>

            <Typography className={styles.noteText}>
              The NHS provides all the treatment necessary to secure and
              maintain your oral health. There are some treatments mainly
              cosmetic that are not normally available under the NHS, and you
              may choose to have these provided privately. You may also choose
              to have some treatment provided privately as an alternative to NHS
              treatment. The dentist will discuss these options with you so that
              you can make an informed choice.
            </Typography>
          </Box>

          <Box className={styles.section}>
            <Typography className={styles.sectionTitle}>
              Proposed NHS Treatment
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box className={styles.subSection}>
                  <Typography className={styles.subSectionTitle}>
                    Diagnosis and Maintenance
                  </Typography>
                  <Box className={styles.checkboxGroup}>
                    {checkboxField("nhs_treatment_examination_advice", "Examination and advice")}
                    {checkboxField("nhs_treatment_radiographs", "Radiographs, study casts & photos")}
                    {checkboxField("nhs_treatment_prevention", "Prevention")}
                    {checkboxField("nhs_treatment_scaling_polishing", "Scaling, polishing, marginal correction of fillings")}
                    {checkboxField("nhs_treatment_adjustment_easing_denture", "Adjustments & easing denture(s)")}
                    {checkboxField("nhs_treatment_diagnosis_other", "Other")}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box className={styles.subSection}>
                  <Typography className={styles.subSectionTitle}>
                    Treatment
                  </Typography>
                  <Box className={styles.checkboxGroup}>
                    {checkboxField("nhs_treatment_non_surgical_periodontal", "Non-Surgical periodontal treatment")}
                    {checkboxField("nhs_treatment_surgical_periodontal", "Surgical periodontal treatment")}
                    {checkboxField("nhs_treatment_permenant_filling", "Permanent fillings & sealant restorations")}
                    {checkboxField("nhs_treatment_endodontics", "Endodontics")}
                    {checkboxField("nhs_treatment_extraction_other_oral", "Extractions & other oral surgery")}
                    {checkboxField("nhs_treatment_occlusal_splints", "Occlusal splints (fabricated in the mouth)")}
                    {checkboxField("nhs_treatment_relining_rebasing", "Relining, rebasing and additions to dentures")}
                    {checkboxField("nhs_treatment_other", "Other")}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box className={styles.subSection}>
                  <Typography className={styles.subSectionTitle}>
                    Appliances
                  </Typography>
                  <Box className={styles.checkboxGroup}>
                    {checkboxField("nhs_treatment_veneers_pinlays", "Veneers, pinlays, inlays, onlays, crowns")}
                    {checkboxField("nhs_treatment_bridges", "Bridges")}
                    {checkboxField("nhs_treatment_dentures", "Dentures")}
                    {checkboxField("nhs_treatment_appliances_other", "Other appliances")}
                  </Box>

                  <TextField
                    fullWidth
                    margin="normal"
                    label="Other"
                    name="nhs_treatment_other_custom"
                    value={formData.nhs_treatment_other_custom}
                    onChange={handleInputChange}
                    multiline
                    minRows={7}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box className={styles.section}>
            <Typography className={styles.sectionTitle}>
              Proposed Private Treatment
            </Typography>

            <Box className={styles.subSection}>
              <Typography className={styles.subSectionTitle}>
                Diagnosis and Maintenance
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth margin="normal" label="Radiographs, study casts & photos £" name="prv_treatment_radiographs" value={formData.prv_treatment_radiographs} onChange={handleInputChange} size="small" type="number" inputProps={{ min: 1 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth margin="normal" label="Prevention £" name="prv_treatment_prevention" value={formData.prv_treatment_prevention} onChange={handleInputChange} size="small" type="number" inputProps={{ min: 1 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth margin="normal" label="Scaling, polishing, marginal correction of fillings £" name="prv_treatment_scaling" value={formData.prv_treatment_scaling} onChange={handleInputChange} size="small" type="number" inputProps={{ min: 1 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth margin="normal" label="Other £" name="diagnosis4" value={formData.diagnosis4} onChange={handleInputChange} size="small" type="number" inputProps={{ min: 1 }} />
                </Grid>
              </Grid>
            </Box>

            <Box className={styles.subSection}>
              <Typography className={styles.subSectionTitle}>
                Treatment
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth margin="normal" label="Non-Surgical periodontal treatment £" name="prv_treatment_non_surgical_periodontal" value={formData.prv_treatment_non_surgical_periodontal} onChange={handleInputChange} size="small" type="number" inputProps={{ min: 1 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth margin="normal" label="Surgical periodontal treatment £" name="prv_treatment_surgical_periodontal" value={formData.prv_treatment_surgical_periodontal} onChange={handleInputChange} size="small" type="number" inputProps={{ min: 1 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth margin="normal" label="Permanent fillings & sealant restorations £" name="prv_treatment_permenant_filling" value={formData.prv_treatment_permenant_filling} onChange={handleInputChange} size="small" type="number" inputProps={{ min: 1 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth margin="normal" label="Endodontics £" name="prv_treatment_endodontics" value={formData.prv_treatment_endodontics} onChange={handleInputChange} size="small" type="number" inputProps={{ min: 1 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth margin="normal" label="Extractions & other oral surgery £" name="prv_treatment_extractions" value={formData.prv_treatment_extractions} onChange={handleInputChange} size="small" type="number" inputProps={{ min: 1 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth margin="normal" label="Other £" name="prv_treatment_other" value={formData.prv_treatment_other} onChange={handleInputChange} size="small" type="number" inputProps={{ min: 1 }} />
                </Grid>
              </Grid>
            </Box>

            <Box className={styles.subSection}>
              <Typography className={styles.subSectionTitle}>
                Appliances
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth margin="normal" label="Veneers, pinlays, inlays, onlays, crowns £" name="prv_treatment_veneers_pinlays" value={formData.prv_treatment_veneers_pinlays} onChange={handleInputChange} size="small" type="number" inputProps={{ min: 1 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth margin="normal" label="Bridges £" name="prv_treatment_bridges" value={formData.prv_treatment_bridges} onChange={handleInputChange} size="small" type="number" inputProps={{ min: 1 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth margin="normal" label="Dentures £" name="prv_treatment_dentures" value={formData.prv_treatment_dentures} onChange={handleInputChange} size="small" type="number" inputProps={{ min: 1 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth margin="normal" label="Other appliances £" name="prv_treatment_appliances_others" value={formData.prv_treatment_appliances_others} onChange={handleInputChange} size="small" type="number" inputProps={{ min: 1 }} />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                margin="normal"
                label="Other"
                name="prv_treatment_other_custom"
                value={formData.prv_treatment_other_custom}
                onChange={handleInputChange}
                multiline
                minRows={8}
              />
            </Box>
          </Box>

          <Box className={styles.section}>
            <Typography className={styles.sectionTitle}>
              Tooth Selection
            </Typography>

            <Box className={styles.toothWrapper}>
              <ToothSelectionGrid
                selectedTeeth={selectedTeeth}
                handleToothSelect={handleToothSelect}
              />
            </Box>

            <TextField
              fullWidth
              margin="normal"
              label="Additional observations"
              name="additional_observations"
              value={formData.additional_observations}
              onChange={handleInputChange}
              multiline
              minRows={7}
            />
          </Box>

          <Box className={styles.section}>
            <Typography className={styles.sectionTitle}>
              Charges
            </Typography>

            <Box className={styles.checkboxGroupHorizontal}>
              {checkboxField("charges_band_1_nhs", "Charges band 1 NHS")}
              {checkboxField("charges_band_2_nhs", "Charges band 2 NHS")}
              {checkboxField("charges_band_3_nhs", "Charges band 3 NHS")}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography className={styles.inputHeading}>
                  Charge for NHS treatment £
                </Typography>
                <TextField
                  fullWidth
                  margin="normal"
                  label="£"
                  name="charge_for_nhs_treatment"
                  value={formData.charge_for_nhs_treatment}
                  onChange={handleInputChange}
                  size="small"
                  type="number"
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={styles.inputHeading}>
                  Charge for Private treatment £
                </Typography>
                <TextField
                  fullWidth
                  margin="normal"
                  label="£"
                  name="charge_for_private_treatment"
                  value={formData.charge_for_private_treatment}
                  onChange={handleInputChange}
                  size="small"
                  type="number"
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box className={styles.footer}>
            <Button variant="outlined" color="secondary" onClick={onClose}>
              Close
            </Button>
            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default FP17DCModal;